import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import adService from "../../api/services/adService";
import Modal from "../../components/common/Modal";
import PaginationBar from "../../components/common/PaginationBar";
import { ActiveFilterPills, FilterSection } from "../../components/UserProfiles";
import { FiEdit2, FiTrash2, FiLoader } from "react-icons/fi";
import { MediaUpload } from "../../components/PromotionalMessage";

const INITIAL_LIST_FILTERS = {
  search: "",
  platform: "",
  status: "",
  mediaType: "",
  /** Web display zone; matches `ads.displayCategory` (see backend `Ad.listAds`) */
  displayCategory: "",
};

/** Admin list filter options — `__legacy__` maps to NULL/empty zone (backend). */
const WEB_ZONE_FILTER_OPTIONS = [
  { value: "", name: "All" },
  { value: "__legacy__", name: "Legacy (empty web zone)" },
  { value: "web_sidebar_right", name: "Desktop — right sidebar (default)" },
  { value: "web_top_banner", name: "Web — top middle banner" },
  { value: "web_mid_banner", name: "Web — bottom middle banner" },
  { value: "web_bottom_right", name: "Web — bottom-right floating" },
];

/** Default web UI zone — matches new-naseebi `ScreenAdSlot displayCategory` for the right rail */
const WEB_DEFAULT_AD_CATEGORY = "web_sidebar_right";

const initialForm = {
  title: "",
  platform: "mobile",
  screenName: "",
  pageName: "",
  status: "active",
  textContent: "",
  // Mobile-only: show ad after N visits (skip first N visits, show on N+1). Stored in textContent as `showAfter=N`.
  showAfterCount: "3",
  mediaType: "none",
  mediaUrl: "",
  ctaText: "",
  ctaUrl: "",
  /** Web: which on-screen slot this single ad fills (one ad per zone per placement; create multiple ads for multiple zones) */
  displayCategory: WEB_DEFAULT_AD_CATEGORY,
};

function parseShowAfterCount(textContent) {
  const raw = String(textContent || "");
  const m = raw.match(/(?:showAfter|show_after|every|frequency)\s*[:=]\s*(\d{1,4})/i);
  if (!m) return "";
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 0 ? String(Math.floor(n)) : "";
}

function upsertShowAfterDirective(textContent, countStr) {
  const existing = String(textContent || "");
  const trimmed = String(countStr || "").trim();
  const without = existing
    // remove any existing directives (keep the rest of the text)
    .replace(/(?:^|\n)\s*(showAfter|show_after|every|frequency)\s*[:=]\s*\d{1,4}\s*(?=\n|$)/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  // Empty => do not include directive
  if (!trimmed) return without;

  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return without;

  const directive = `showAfter=${Math.floor(n)}`;
  if (!without) return directive;
  return `${directive}\n\n${without}`.trim();
}

function listFilterLabel(key, value) {
  if (key === "platform") {
    const map = { mobile: "Mobile", web: "Web" };
    return map[value] || value;
  }
  if (key === "status") {
    const map = { active: "Active", inactive: "Inactive" };
    return map[value] || value;
  }
  if (key === "mediaType") {
    const map = { image: "Image", video: "Video", none: "No media" };
    return map[value] || value;
  }
  if (key === "displayCategory") {
    const opt = WEB_ZONE_FILTER_OPTIONS.find((o) => o.value === value);
    return opt?.name || value;
  }
  return value;
}

function encodePlacementKey(screenName, pagePath) {
  const s = String(screenName || "").trim();
  const p = pagePath == null || pagePath === "" ? "" : String(pagePath).trim();
  return `${s}::__::${p}`;
}

/** Case-insensitive match: stored ad key vs catalog row (fixes edit when DB casing differs). */
function rowMatchesCatalogKey(storedKey, o) {
  const raw = String(storedKey || "");
  const sep = "::__::";
  const idx = raw.indexOf(sep);
  const s = idx >= 0 ? raw.slice(0, idx) : raw;
  const p = idx >= 0 ? raw.slice(idx + sep.length) : "";
  return (
    String(o.screenName || "").trim().toLowerCase() === String(s).trim().toLowerCase() &&
    String(o.pagePath || "").trim().toLowerCase() === String(p).trim().toLowerCase()
  );
}

function canonicalKeyForRow(o) {
  return encodePlacementKey(o.screenName, o.pagePath);
}

/** Normalize ad.platform for the editor (DB may send mixed case). */
function normalizeEditorPlatform(value) {
  const p = String(value || "mobile").toLowerCase();
  if (p === "web" || p === "mobile") return p;
  return "mobile";
}

/** Locales used by the public web app (next-intl); paths already starting with one of these are left as-is. */
const WEB_LOCALE_SEGMENTS = ["en", "ur", "ar", "hi", "fr"];

/**
 * For web ads, ensure relative pathnames include the default locale prefix `/en` so CTAs match routes like `/en/notifications`.
 * Skips absolute URLs and mobile-style paths (no leading slash).
 */
function normalizeWebCtaUrl(ctaUrl, platform) {
  if (normalizeEditorPlatform(platform) !== "web") return String(ctaUrl ?? "");
  const raw = String(ctaUrl ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (!raw.startsWith("/")) return raw;
  const segments = raw.split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first && WEB_LOCALE_SEGMENTS.includes(first)) return raw;
  if (raw === "/") return "/en";
  return `/en${raw}`;
}

/** Stable CTA URL string for a catalog row (disambiguates mobile slots that share the same pagePath). */
function suggestedCtaUrl(o, formPlatform) {
  const path = o.pagePath != null && String(o.pagePath).trim() !== "" ? String(o.pagePath).trim() : "";
  const webStyle = formPlatform === "web";
  if (webStyle) {
    return normalizeWebCtaUrl(path || "/", "web");
  }
  if (path) {
    return `${String(o.screenName || "").trim()}/${path}`;
  }
  return String(o.screenName || "").trim();
}

function buildPlacementsPayload(keys, form) {
  const fromKeys = (Array.isArray(keys) ? keys : [])
    .map((k) => {
      const [s, p] = String(k || "").split("::__::");
      return {
        screenName: String(s || "").trim(),
        pageName: String(p || "").trim() || null,
      };
    })
    .filter((x) => !!x.screenName);
  if (fromKeys.length) return fromKeys;
  if (form.screenName?.trim()) {
    return [{ screenName: form.screenName.trim(), pageName: form.pageName?.trim() || null }];
  }
  return [];
}

function formatPlacementsCell(ad) {
  const list = Array.isArray(ad.placements) && ad.placements.length
    ? ad.placements
    : [{ screenName: ad.screenName, pageName: ad.pageName || null }];
  return list
    .map((p) => {
      const s = p.screenName || "";
      const path = p.pageName ? ` ${p.pageName}` : "";
      return `${s}${path}`.trim();
    })
    .filter(Boolean)
    .join(" · ");
}

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [filters, setFilters] = useState(INITIAL_LIST_FILTERS);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [creatingPlacementKeys, setCreatingPlacementKeys] = useState([]);
  const [modalPlacementOptions, setModalPlacementOptions] = useState([]);
  const [modalDisplayCategories, setModalDisplayCategories] = useState([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [backendLimit, setBackendLimit] = useState(15);
  const [backendPage, setBackendPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, ad: null });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const isFetchingRef = React.useRef(false);

  const fetchAds = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const res = await adService.list({
        page,
        limit: 15,
        search: filters.search?.trim() || undefined,
        platform: filters.platform || undefined,
        status: filters.status || undefined,
        mediaType: filters.mediaType || undefined,
        displayCategory: filters.displayCategory?.trim() || undefined,
      });
      setAds(res?.ads || []);
      setTotalPages(res?.totalPages || 1);
      setTotal(res?.total || 0);
      setBackendLimit(res?.limit || 15);
      setBackendPage(res?.page || 1);
    } catch (e) {
      toast.error(e?.message || "Failed to load ads");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, filters.search, filters.platform, filters.status, filters.mediaType, filters.displayCategory]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  useEffect(() => {
    if (!modalOpen) return;
    let cancelled = false;
    (async () => {
      setPlacementsLoading(true);
      try {
        const platform = normalizeEditorPlatform(form.platform);
        if (platform === "web") {
          const res = await adService.placements("web");
          if (cancelled) return;
          setModalPlacementOptions(Array.isArray(res.placements) ? res.placements : []);
          setModalDisplayCategories(Array.isArray(res.displayCategories) ? res.displayCategories : []);
        } else if (platform === "mobile") {
          const res = await adService.placements("mobile");
          if (cancelled) return;
          setModalPlacementOptions(Array.isArray(res.placements) ? res.placements : []);
          setModalDisplayCategories([]);
        } else {
          const res = await adService.placements();
          if (cancelled) return;
          const merged = [
            ...(Array.isArray(res.mobile) ? res.mobile : []),
            ...(Array.isArray(res.web) ? res.web : []),
          ];
          setModalPlacementOptions(merged);
          setModalDisplayCategories(Array.isArray(res.displayCategories) ? res.displayCategories : []);
        }
      } catch {
        if (!cancelled) {
          setModalPlacementOptions([]);
          setModalDisplayCategories([]);
        }
      } finally {
        if (!cancelled) setPlacementsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, form.platform]);

  /** After catalog loads, normalize selected keys to canonical catalog keys (keeps unknown keys as-is). */
  useEffect(() => {
    if (!modalOpen || placementsLoading || !modalPlacementOptions.length) return;
    setCreatingPlacementKeys((prev) => {
      const seen = new Set();
      const out = [];
      for (const k of prev) {
        const row = modalPlacementOptions.find((o) => rowMatchesCatalogKey(k, o));
        const nextKey = row ? canonicalKeyForRow(row) : k;
        if (!seen.has(nextKey)) {
          seen.add(nextKey);
          out.push(nextKey);
        }
      }
      return out;
    });
  }, [modalOpen, placementsLoading, modalPlacementOptions]);

  const adsFilterPrimaryFields = useMemo(
    () => [
      {
        name: "search",
        label: "Search",
        type: "text",
        placeholder: "Ad title or numeric ad ID…",
        className: "min-w-[200px] max-w-[min(100%,380px)] w-[min(100%,380px)] shrink-0",
      },
      {
        name: "platform",
        label: "Platform",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "mobile", name: "Mobile" },
          { value: "web", name: "Web" },
        ],
        className: "w-[118px] shrink-0",
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "active", name: "Active" },
          { value: "inactive", name: "Inactive" },
        ],
        className: "w-[118px] shrink-0",
      },
      {
        name: "mediaType",
        label: "Media type",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "image", name: "Image" },
          { value: "video", name: "Video" },
          { value: "none", name: "No media" },
        ],
        className: "w-[128px] shrink-0",
      },
      {
        name: "displayCategory",
        label: "Web zone",
        type: "select",
        options: WEB_ZONE_FILTER_OPTIONS,
        className: "min-w-[200px] max-w-[280px] w-[min(100%,280px)] shrink-0",
      },
    ],
    []
  );

  const handleFilterChange = useCallback((e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_LIST_FILTERS);
    setPage(1);
  }, []);

  const removeFilter = useCallback((key) => {
    setFilters((prev) => ({ ...prev, [key]: INITIAL_LIST_FILTERS[key] ?? "" }));
    setPage(1);
  }, []);

  const activeFilterEntries = useMemo(
    () =>
      Object.keys(filters).filter(
        (key) => filters[key] !== "" && filters[key] != null
      ),
    [filters]
  );

  const getFilterLabel = useCallback((key, value) => listFilterLabel(key, value), []);

  const getActiveFiltersCount = useCallback(
    () => activeFilterEntries.length,
    [activeFilterEntries]
  );

  const openCreate = () => {
    setSelectedAd(null);
    setForm(initialForm);
    setCreatingPlacementKeys([]);
    setModalPlacementOptions([]);
    setModalOpen(true);
  };

  const openEdit = (ad) => {
    setSelectedAd(ad);
    setModalPlacementOptions([]);
    setForm({
      ...initialForm,
      title: ad.title || "",
      platform: normalizeEditorPlatform(ad.platform),
      screenName: ad.screenName || "",
      pageName: ad.pageName || "",
      status: ad.status || "active",
      textContent: ad.textContent || "",
      showAfterCount: normalizeEditorPlatform(ad.platform) === "mobile" ? parseShowAfterCount(ad.textContent) : "",
      mediaType: ad.mediaType || "none",
      mediaUrl: ad.mediaUrl || "",
      ctaText: ad.ctaText || "",
      ctaUrl: normalizeWebCtaUrl(ad.ctaUrl || "", normalizeEditorPlatform(ad.platform)),
      displayCategory:
        normalizeEditorPlatform(ad.platform) === "web"
          ? String(ad.displayCategory || "").trim() || WEB_DEFAULT_AD_CATEGORY
          : "",
    });
    const pl = Array.isArray(ad.placements) && ad.placements.length
      ? ad.placements
      : [{ screenName: ad.screenName, pageName: ad.pageName || null }];
    setCreatingPlacementKeys(
      pl.map((p) => encodePlacementKey(p.screenName, p.pageName))
    );
    setModalOpen(true);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await adService.uploadMedia(file);
      setForm((f) => ({
        ...f,
        mediaUrl: res.mediaUrl,
        mediaType: res.mediaType === "video" ? "video" : "image",
      }));
      toast.success("Media uploaded");
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveAd = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    const placementsPayload = buildPlacementsPayload(creatingPlacementKeys, form);
    if (!placementsPayload.length) {
      toast.error("Select at least one screen/path (or fill screen name)");
      return;
    }
    const primary = placementsPayload[0];
    const normalizedPlatform = normalizeEditorPlatform(form.platform);
    const nextTextContent =
      normalizedPlatform === "mobile"
        ? upsertShowAfterDirective(form.textContent, form.showAfterCount)
        : form.textContent;
    const payload = {
      ...form,
      screenName: primary.screenName,
      pageName: primary.pageName,
      placements: placementsPayload,
      textContent: nextTextContent,
      mediaType: form.mediaUrl ? form.mediaType : "none",
      mediaUrl: form.mediaUrl || null,
      ctaUrl: normalizeWebCtaUrl(form.ctaUrl, normalizedPlatform),
      displayCategory:
        normalizedPlatform === "web"
          ? String(form.displayCategory || "").trim() || WEB_DEFAULT_AD_CATEGORY
          : null,
    };
    try {
      setSaving(true);
      if (selectedAd) {
        await adService.update(selectedAd.id, payload);
        toast.success("Ad updated");
      } else {
        await adService.create(payload);
        toast.success(placementsPayload.length > 1 ? "Ad created for multiple screens" : "Ad created");
      }
      setModalOpen(false);
      fetchAds();
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const placementSelectValue = useMemo(() => {
    const formKey = encodePlacementKey(form.screenName, form.pageName);
    const match = modalPlacementOptions.find((o) => rowMatchesCatalogKey(formKey, o));
    return match ? canonicalKeyForRow(match) : "__custom__";
  }, [form.screenName, form.pageName, modalPlacementOptions]);

  const placementUiCopy = useMemo(() => {
    const p = normalizeEditorPlatform(form.platform);
    if (p === "web") {
      return {
        showOnTitle: "Show this ad on (web)",
        showOnHint: "Select page paths from the catalog below.",
        quickPickLabel: "Quick pick — web path",
        screenLabel: "Screen / route key (web)",
        screenPh: "e.g. home, notifications",
        pathLabel: "Page path",
        pathPh: "e.g. /notifications, /home",
        ctaUrlPh: "Web path (saved with /en prefix), e.g. /en/blogs, or full URL",
      };
    }
    return {
      showOnTitle: "Show this ad on (mobile)",
      showOnHint: "Select native screens and ad slots (banner, sheet, …).",
      quickPickLabel: "Quick pick — screen / slot",
      screenLabel: "Screen name (mobile)",
      screenPh: "e.g. Home, Explore",
      pathLabel: "Ad slot",
      pathPh: "e.g. banner, sheet, fullscreen",
      ctaUrlPh: "e.g. Home/banner",
    };
  }, [form.platform]);

  const ctaUrlSelectValue = useMemo(() => {
    const want = String(form.ctaUrl || "").trim();
    if (!want) return "__custom__";
    const plat = normalizeEditorPlatform(form.platform);
    const match = modalPlacementOptions.find(
      (o) => suggestedCtaUrl(o, plat) === want
    );
    return match ? canonicalKeyForRow(match) : "__custom__";
  }, [form.ctaUrl, form.platform, modalPlacementOptions]);

  const ctaNameSelectValue = useMemo(() => {
    const want = String(form.ctaText || "").trim();
    if (!want) return "__custom__";
    const match = modalPlacementOptions.find((o) => String(o.label || "").trim() === want);
    return match ? canonicalKeyForRow(match) : "__custom__";
  }, [form.ctaText, modalPlacementOptions]);

  const applyPlacementOptionToCta = (o) => {
    if (!o) return;
    setForm((f) => ({
      ...f,
      ctaUrl: suggestedCtaUrl(o, normalizeEditorPlatform(f.platform)),
      ctaText: o.label || f.ctaText,
    }));
  };

  return (
    <div className="w-full p-4 overflow-x-hidden">
      <div className="bg-white rounded-b-xl rounded-t-none sm:rounded-t-xl shadow-md border border-t-0 border-gray-200 overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h2 className="text-md md:text-xl font-bold text-gray-800 tracking-tight">Ads</h2>
              <p className="text-xs md:text-sm text-gray-500">One ad can appear on multiple screens or paths</p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="cursor-pointer text-xs md:text-sm rounded-lg border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md"
            >
              Create Ad
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Filters</h3>
            {getActiveFiltersCount() > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs md:text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>

          <ActiveFilterPills
            activeKeys={activeFilterEntries}
            filters={filters}
            getLabel={getFilterLabel}
            onRemove={removeFilter}
          />

          <FilterSection
            primaryFields={adsFilterPrimaryFields}
            secondaryFields={[]}
            values={filters}
            onChange={handleFilterChange}
            onApply={() => setPage(1)}
            onClear={clearFilters}
            applyLabel="Apply"
            clearLabel="Clear All"
            singleRow
          />

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent" />
                <p className="mt-4 text-gray-600 font-medium text-sm">Loading ads...</p>
              </div>
            ) : (
              <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="text-left border-b bg-gray-50 text-[11px] uppercase text-gray-600">
                    <th className="py-3 px-3">SR. NO</th>
                    <th className="py-3 px-3">Title</th>
                    <th className="px-3 min-w-[200px] max-w-[420px]">Screens / paths</th>
                    <th className="px-3">Media</th>
                    <th className="px-3">Text</th>
                    <th className="px-3">Platform</th>
                    <th className="px-3">Web zone</th>
                    <th className="px-3">Status</th>
                    <th className="px-3">Views</th>
                    <th className="px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && ads.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="p-4 text-center text-xs md:text-sm text-gray-500">
                        No ads found
                      </td>
                    </tr>
                  ) : (
                    ads.map((ad,idx) => (
                      <tr key={ad.id} className="border-b hover:bg-gray-50">
                        <td className="px-3">
                        {(backendPage - 1) * backendLimit + idx + 1}
                        </td>
                        <td className="py-3 px-3 font-semibold">{ad.title}</td>
                        <td className="px-3 text-xs text-gray-700 align-top" title={formatPlacementsCell(ad)}>
                          <span className="line-clamp-4">{formatPlacementsCell(ad)}</span>
                        </td>
                        <td className="px-3">
                          {ad.mediaUrl ? (
                            ad.mediaType === "video" ? (
                              <video src={ad.mediaUrl} className="w-20 h-12 rounded object-cover bg-black" muted />
                            ) : (
                              <img src={ad.mediaUrl} alt={ad.title} className="w-20 h-12 rounded object-cover" />
                            )
                          ) : (
                            <span className="text-gray-400">No media</span>
                          )}
                        </td>
                        <td className="max-w-[260px] truncate text-gray-600 px-3" title={ad.textContent || ""}>
                          {ad.textContent || "-"}
                        </td>
                        <td className="px-3">{ad.platform}</td>
                        <td className="px-3 text-xs text-gray-600 max-w-[140px] truncate" title={ad.displayCategory || ""}>
                          {String(ad.platform || "").toLowerCase() === "web" && ad.displayCategory
                            ? ad.displayCategory
                            : String(ad.platform || "").toLowerCase() === "web"
                              ? "—"
                              : "—"}
                        </td>
                        <td className="px-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              ad.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {ad.status}
                          </span>
                        </td>
                        <td className="px-3">
                          {ad.totalViews || 0} ({ad.uniqueUsers || 0} unique)
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-3 items-center">
                            <button
                              type="button"
                              onClick={() => openEdit(ad)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete({ open: true, ad })}
                              className="text-red-600 hover:text-red-700"
                              title="Delete"
                            >
                              {deletingId === ad.id ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={15}
            label="ads"
            onPageChange={setPage}
          />
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={selectedAd ? "Edit Ad" : "Create Ad"}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button
              type="button"
              disabled={saving}
              onClick={() => setModalOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={saveAd}
              className="rounded-lg border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving ? <FiLoader className="animate-spin" /> : null}
              {selectedAd ? "Save" : "Create Ad"}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-3">
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Ad title"
            className="rounded border px-3 py-2"
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              value={normalizeEditorPlatform(form.platform)}
              onChange={(e) => {
                const p = normalizeEditorPlatform(e.target.value);
                setForm((f) => ({
                  ...f,
                  platform: p,
                  displayCategory: p === "web" ? f.displayCategory || WEB_DEFAULT_AD_CATEGORY : "",
                }));
              }}
              className="rounded border px-3 py-2"
            >
              <option value="mobile">Mobile</option>
              <option value="web">Web</option>
            </select>
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="rounded border px-3 py-2"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <p className="text-[11px] text-gray-500 -mt-1">
            Editing uses this platform to load the right catalog:{" "}
            <strong>Mobile</strong> = screen + slot names, <strong>Web</strong> = pathname list,{" "}
          </p>

          {normalizeEditorPlatform(form.platform) === "web" ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 space-y-1.5">
              <label className="text-xs font-semibold text-amber-900">Display zone (web)</label>
              <p className="text-[11px] text-amber-900/80">
                One ad fills <strong>one</strong> zone. For three zones on the same page, create <strong>three</strong> ads
                with different zones and the same (or overlapping) screens/paths. The same zone can be used across
                multiple selected routes.
              </p>
              <select
                value={form.displayCategory || WEB_DEFAULT_AD_CATEGORY}
                onChange={(e) => setForm((f) => ({ ...f, displayCategory: e.target.value }))}
                className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm"
              >
                {(modalDisplayCategories.length
                  ? modalDisplayCategories
                  : [{ id: WEB_DEFAULT_AD_CATEGORY, label: "Desktop — right sidebar (default)" }]
                ).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {placementsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 py-2">
              <FiLoader className="animate-spin" />
              Loading screens & paths…
            </div>
          ) : null}

          <label className="text-xs font-semibold text-gray-600">{placementUiCopy.showOnTitle}</label>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold text-gray-700 mb-1">Select one or more screens / paths</p>
            <p className="text-[11px] text-gray-500 mb-2">{placementUiCopy.showOnHint}</p>
            <div className="max-h-[220px] overflow-y-auto grid grid-cols-1 gap-1.5">
              {modalPlacementOptions.map((o) => {
                const key = canonicalKeyForRow(o);
                const checked = creatingPlacementKeys.some((k) => rowMatchesCatalogKey(k, o));
                return (
                  <label
                    key={key}
                    className={`flex items-start gap-2 text-xs md:text-sm rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${
                      checked
                        ? "bg-red-50 border-red-300 text-red-900"
                        : "bg-white border-gray-200 hover:bg-gray-50 text-gray-800"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      onChange={(e) => {
                        setCreatingPlacementKeys((prev) => {
                          const without = prev.filter((k) => !rowMatchesCatalogKey(k, o));
                          if (!e.target.checked) {
                            const arr = without;
                            if (arr.length > 0) {
                              const last = arr[arr.length - 1];
                              const [s, p] = String(last).split("::__::");
                              setForm((f) => ({ ...f, screenName: s || "", pageName: (p || "").trim() }));
                            } else {
                              setForm((f) => ({ ...f, screenName: "", pageName: "" }));
                            }
                            return arr;
                          }
                          const arr = [...without, key];
                          setForm((f) => ({
                            ...f,
                            screenName: String(o.screenName || ""),
                            pageName: String(o.pagePath || ""),
                          }));
                          return arr;
                        });
                      }}
                    />
                    <span className="flex flex-col min-w-0">
                      <span className="font-semibold truncate">{o.label}</span>
                      <span className="text-[11px] text-gray-500 truncate">
                        {o.screenName}
                        {o.pagePath ? ` · ${o.pagePath}` : ""}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              One ad — same creative — shown on every selected screen/path.
            </p>
          </div>
          <label className="text-xs font-semibold text-gray-600">{placementUiCopy.quickPickLabel}</label>
          <select
            value={placementSelectValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__custom__") return;
              const row = modalPlacementOptions.find((opt) => canonicalKeyForRow(opt) === v);
              if (!row) return;
              setForm((f) => ({
                ...f,
                screenName: String(row.screenName || ""),
                pageName: String(row.pagePath || ""),
              }));
              setCreatingPlacementKeys((prev) => {
                const key = canonicalKeyForRow(row);
                return prev.includes(key) ? prev : [...prev, key];
              });
            }}
            className="rounded border px-3 py-2 text-sm"
          >
            <option value="__custom__">Custom — use fields below</option>
            {modalPlacementOptions.map((o) => (
              <option key={canonicalKeyForRow(o)} value={canonicalKeyForRow(o)}>
                {o.label} ({o.screenName}
                {o.pagePath ? ` / ${o.pagePath}` : ""})
              </option>
            ))}
          </select>
          <label className="text-[11px] font-semibold text-gray-600">{placementUiCopy.screenLabel}</label>
          <input
            value={form.screenName}
            onChange={(e) => setForm((f) => ({ ...f, screenName: e.target.value }))}
            placeholder={placementUiCopy.screenPh}
            className="rounded border px-3 py-2"
          />
          <label className="text-[11px] font-semibold text-gray-600">{placementUiCopy.pathLabel}</label>
          <input
            value={form.pageName}
            onChange={(e) => setForm((f) => ({ ...f, pageName: e.target.value }))}
            placeholder={placementUiCopy.pathPh}
            className="rounded border px-3 py-2"
          />
          {normalizeEditorPlatform(form.platform) === "mobile" ? (
            <>
              <label className="text-[11px] font-semibold text-gray-600">
                Show after N visits (mobile)
                <span className="ml-2 font-normal text-gray-500">
                  e.g. 5 = show on the 6th visit
                </span>
              </label>
              <input
                inputMode="numeric"
                value={form.showAfterCount}
                onChange={(e) => {
                  const v = e.target.value;
                  // allow empty, otherwise digits only
                  if (v === "" || /^\d{0,4}$/.test(v)) {
                    setForm((f) => ({ ...f, showAfterCount: v }));
                  }
                }}
                placeholder="0 (show every time)"
                className="rounded border px-3 py-2"
              />
            </>
          ) : null}
          <textarea
            value={form.textContent}
            onChange={(e) => setForm((f) => ({ ...f, textContent: e.target.value }))}
            placeholder="Ad text (optional)"
            className="rounded border px-3 py-2 min-h-[80px]"
          />
          <div className="flex flex-col gap-2">
            <label className="block text-xs md:text-sm font-semibold text-gray-700">
              Media (image or video)
              {uploading ? (
                <span className="ml-2 inline-flex items-center gap-1 text-red-600 font-normal">
                  <FiLoader className="animate-spin" /> Uploading…
                </span>
              ) : null}
            </label>
            <MediaUpload
              type={form.mediaType === "video" ? "video" : "image"}
              inputId="ad-media-upload"
              accept="image/*,video/*"
              hint="JPG, PNG or MP4 up to 50MB"
              uploadLabel="Upload ad media"
              onSelect={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              onRemove={() =>
                setForm((f) => ({
                  ...f,
                  mediaUrl: "",
                  mediaType: "none",
                }))
              }
              preview={form.mediaUrl || ""}
            />
          </div>

          <label className="text-xs font-semibold text-gray-600">CTA</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-700">CTA URL (pathname)</span>
              <select
                value={ctaUrlSelectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__custom__") return;
                  const o = modalPlacementOptions.find((opt) => canonicalKeyForRow(opt) === v);
                  applyPlacementOptionToCta(o);
                }}
                className="rounded border px-3 py-2 text-sm bg-white"
              >
                <option value="__custom__">Select path…</option>
                {modalPlacementOptions.map((o) => (
                  <option key={`cta-url-${canonicalKeyForRow(o)}`} value={canonicalKeyForRow(o)}>
                    {suggestedCtaUrl(o, normalizeEditorPlatform(form.platform))} — {o.label}
                  </option>
                ))}
              </select>
              <input
                value={form.ctaUrl}
                onChange={(e) => setForm((f) => ({ ...f, ctaUrl: e.target.value }))}
                onBlur={() => {
                  if (normalizeEditorPlatform(form.platform) !== "web") return;
                  setForm((f) => ({ ...f, ctaUrl: normalizeWebCtaUrl(f.ctaUrl, "web") }));
                }}
                placeholder={placementUiCopy.ctaUrlPh}
                className="rounded border px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-700">CTA name (label)</span>
              <select
                value={ctaNameSelectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__custom__") return;
                  const o = modalPlacementOptions.find((opt) => canonicalKeyForRow(opt) === v);
                  applyPlacementOptionToCta(o);
                }}
                className="rounded border px-3 py-2 text-sm bg-white"
              >
                <option value="__custom__">Select screen name…</option>
                {modalPlacementOptions.map((o) => (
                  <option key={`cta-name-${canonicalKeyForRow(o)}`} value={canonicalKeyForRow(o)}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                value={form.ctaText}
                onChange={(e) => setForm((f) => ({ ...f, ctaText: e.target.value }))}
                placeholder="Or type CTA text (e.g. Go to Blogs)"
                className="rounded border px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmDelete.open}
        title="Delete Ad"
        onClose={() => setConfirmDelete({ open: false, ad: null })}
        footer={
          <>
            <button
              type="button"
              disabled={deletingId != null}
              onClick={() => setConfirmDelete({ open: false, ad: null })}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!confirmDelete.ad?.id) return;
                try {
                  setDeletingId(confirmDelete.ad.id);
                  await adService.remove(confirmDelete.ad.id);
                  toast.success("Ad deleted");
                  setConfirmDelete({ open: false, ad: null });
                  fetchAds();
                } catch (e) {
                  toast.error(e?.message || "Delete failed");
                } finally {
                  setDeletingId(null);
                }
              }}
              disabled={deletingId != null}
              className="rounded-lg border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2"
            >
              {deletingId != null ? <FiLoader className="animate-spin" /> : null}
              Delete
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Are you sure you want to delete this ad? All placements and view history will also be removed.
        </p>
      </Modal>
    </div>
  );
}
